import { withDefaults } from '../../../core/theme';
import { defineEffect } from '../../../core/effect';
import { seeded, seg, tween } from '../../../core/effect/motion';
import { parseZoneKey } from '../../../core/field/zones';
import {
  CARD_ASPECT, add, card, fade, fill,
} from '../fx/common';
import { OBJECT_STYLE, card as boardCard } from '../drawObjects';
import { sourceOf, topOf, zoneCenter } from '../fx/cardSource';

/**
 * **場に出る**（Issue #561。指示書 `../../direction/move.md` の「場に出る」）。
 * 出どころからカードが現れ、弧を描いて行き先のゾーンへ飛び、置かれるときの向き・表裏に合わせて回って置かれる。
 * 召喚・特殊召喚・セット・発動の演出は、置かれたあとの見せ場に専念する（ユーザー 2026-09-28）。
 *
 * ```
 * 0     出どころにカードが現れる（手札 = HUD の手札の位置 / 墓地・EX・デッキ = 盤の上の山 / モンスターゾーン）
 * 60    弧を描いて行き先へ飛ぶ（位置の進みは ease-out: 入りは速く、置く手前でゆっくり → 直前に軽く沈む）。置かれるときの向き（守備なら横・伏せた魔法罠は 180°）へ回り、
 *       裏 → 表なら長い辺を軸に返る
 * 340   置かれる: ここで盤面が進み、盤のカードに引き継ぐ（最後のフレームは盤のカードと同じ位置・向き・大きさ・絵）
 * 340〜400 盤のカードが軽く沈んで戻る。硬い輪と火花・小さな揺れ（セットは静かに＝沈むだけ）
 * 400   終わり（何も残らない）
 * ```
 *
 * **置かれるまで盤面は移動前**（口 `board` が `{ at: LAND }`。Issue #566）なので、飛ぶカードは演出の板で描く
 * （行き先のゾーンはまだ空）。板の絵は盤のカードと同じ描き方（`drawObjects.js::card`）で、
 * 表裏・守備・ATK / DEF の帯は移動後の盤面（`opens` が `next` に入れる）から。
 * 出どころが盤のカード（モンスター → 魔法・罠）なら、飛んでいる間は盤のカードを借りて隠す。
 * 山（墓地など）の一番上は借りられない（板の id が山ごと）ので、置かれるまで山に残って見える。
 */
export const D = 400;
export const LAND = 340;
const APPEAR = 60;
const SETTLE = D - LAND;
const SPARKS = 10;

const FIELD = ['monster', 'magic_trap', 'field'];

const v = withDefaults(() => [OBJECT_STYLE]);

/** 盤のカードを借りるか（出どころが盤の 1 枚のゾーン＝モンスター → 魔法・罠など） */
const borrowsSource = (payload) => Boolean(payload?.uid && FIELD.includes(parseZoneKey(payload?.fromZone)?.kind));

/** 行き先で置かれる姿（移動後の盤面 → 無ければ payload の表示形式） */
export function landingOf(payload) {
  const n = payload?.next ?? {};
  const pos = payload?.position ?? null;
  return {
    code: n.code ?? payload?.code ?? null,
    faceUp: n.faceUp != null ? Boolean(n.faceUp) : pos?.faceUp !== false,
    defense: n.defense != null ? Boolean(n.defense) : Boolean(pos?.defense),
    stats: n.stats ?? null,
  };
}

/** セット（行き先が裏）＝静かに置く */
export const isQuiet = (payload) => !landingOf(payload).faceUp;

/** 出どころで見えている面（手札・デッキ・EX は裏。墓地・除外・場は移動前の盤面の表裏） */
export function startFaceUp(payload) {
  const k = parseZoneKey(payload?.fromZone)?.kind ?? payload?.fromKind;
  if (k === 'hand' || k === 'maindeck' || k === 'extra') return false;
  return payload?.prev?.faceUp !== false;
}

/** 盤の上の回し（`ObjectLayer.js::cardTurn` と同じ規則） */
function turnOf(zone, c) {
  if (c.defense) return Math.PI / 2;
  const kind = parseZoneKey(zone)?.kind;
  if (!c.faceUp && (kind === 'magic_trap' || kind === 'field')) return Math.PI;
  return 0;
}



/**
 * 盤のカードと同じ描き方の板（`drawObjects.js::card`）。canvas が使えなければ（jest）ふつうのカードの板。
 * @returns three の Mesh（盤に寝かせてある）
 */
function faceMesh(ctx, { zone, player, c, w, h }) {
  const { kit } = ctx;
  const spec = boardCard({
    item: { card: c, zone, player },
    scene: { cards: ctx.cards, players: ctx.hud?.players },
    style: ctx.style,
  });
  const bw = Math.max(1, Math.round(w * 2));
  const bh = Math.max(1, Math.round(h * 2));
  const cv = typeof document !== 'undefined' ? document.createElement('canvas') : null;
  const g2d = cv ? (cv.width = bw, cv.height = bh, cv.getContext?.('2d')) : null;
  if (g2d && spec?.draw) {
    try {
      spec.draw(g2d, { size: { w: bw, h: bh } });
      const tex = kit.textureFromCanvas(cv);
      return kit.onBoard(kit.quad(kit.plain(tex, 1), w, h));
    } catch { /* 描けなければふつうの板 */ }
  }
  return kit.onBoard(card(ctx, { code: c.faceUp ? c.code : null, player, faceUp: c.faceUp, w }).mesh);
}

/** 板を線分 a → b の上に置く（盤の上。`[x, z]`、幅 wd） */
function lay(m, a, b, wd, y) {
  const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
  m.position.set((a[0] + b[0]) / 2, y, (a[1] + b[1]) / 2);
  m.rotation.y = -Math.atan2(b[1] - a[1], b[0] - a[0]);
  m.scale.set(Math.max(0.001, len), 1, Math.max(0.001, wd));
}

/** ease-out（入りは速く・着地の手前でゆっくり） */
const powerOut = (n) => (u) => 1 - (1 - u) ** n;
const EASE_ABOVE = powerOut(4);   // 画面の上の外から: 早く画面に入って、そこから長く見せる
const EASE_ARC = powerOut(3);     // 盤・HUD から弧を描いて

/**
 * 飛ぶ位置の進み 0 → 1（帯の時刻 t。LAND で 1）。**ease-out**（ユーザー 2026-09-28）:
 * 入りは速く、置く手前で減速する。`via` は出どころの決め方（`sourceOf`）
 */
export function flightAt(t, via = 'field') {
  if (t >= LAND) return 1;
  return tween(t, APPEAR * 0.5, LAND, via === 'above' ? EASE_ABOVE : EASE_ARC);
}

/** 置く直前の軽い沈み（上面からの下げ。LAND - 70 → LAND で 0 → 最大 → 0。LAND ちょうどで盤のカードの高さ） */
export const dipAt = (t) => (t < LAND - 70 || t >= LAND ? 0 : Math.sin(Math.PI * seg(t, LAND - 70, LAND)) * 1.4);

/** 置かれたあとの沈み（上面からの下げ。0 → 最大 → 0） */
export const sinkAt = (t) => (t < LAND || t >= D ? 0 : Math.sin(Math.PI * seg(t, LAND, D)) * (1 - seg(t, LAND, D)) * 1.6);

export default defineEffect({
  situation: 'move_enter',
  role: 'motion',
  label: '場に出る（出どころから飛んで置かれる）',
  color: '#b18cff',
  duration: () => D,
  sounds: ({ payload }) => (isQuiet(payload) ? [{ at: LAND - 20, name: 'set_card', gain: 0.7 }] : []),
  scene: ({ payload }) => (isQuiet(payload) ? undefined : ['reset', { ms: LAND, shake: { amp: 4, freq: 30, decayMs: SETTLE } }]),
  // 置かれた瞬間に盤面をイベントの後へ（それまで行き先のゾーンは空）
  board: () => ({ at: LAND }),
  /*
   * 盤のカードを借りる（R13）。置かれるまでは出どころの板を隠し（盤のカードからの移動）、
   * 置かれたあとは行き先の板を軽く沈めて戻す。**そのゾーンの板にだけ**当てる
   */
  tweaks: ({ payload, localMs: t }) => {
    if (!payload?.uid || t < 0 || t >= D) return {};
    if (t < LAND) {
      if (!borrowsSource(payload)) return {};
      const id = `card:${payload.fromZone}`;
      return { [payload.uid]: (h) => { if (h.part?.id && h.part.id !== id) return; h.root.visible = false; } };
    }
    const id = `card:${payload.zone}`;
    const dy = sinkAt(t);
    return {
      [payload.uid]: (h) => {
        if (h.part?.id && h.part.id !== id) return;
        h.reset?.();
        h.root.position.y -= dy;
      },
    };
  },
  layers: [
    {
      id: 'enter',
      label: '場に出る（飛ぶカード・着地の輪と火花）',
      space: 'board',
      script: (ctx) => {
        const { THREE } = ctx;
        const root = new THREE.Group();
        const { payload } = ctx;
        const f = ctx.stage?.field;
        const unit = f?.unit > 0 ? f.unit : 0;
        const plane = f?.plane ?? null;
        const zone = payload?.zone;
        const center = zoneCenter(f, zone);
        if (!center || !(unit > 0) || !plane) return { root };
        const to = parseZoneKey(zone);
        const land = landingOf(payload);
        const quiet = !land.faceUp;
        const cw = unit;
        const ch = unit / CARD_ASPECT;
        const yTop = topOf(zone, land, ctx.style) + 0.05;
        const end = [center[0] - plane.w / 2, yTop, center[1] - plane.h / 2];
        const base = Number(f.cardAngle?.(zone)) || 0;
        const yawEnd = base + turnOf(zone, land);
        const src = sourceOf(ctx, plane, end);
        const start = src.world;
        const dist = Math.hypot(end[0] - start[0], end[2] - start[2]);
        const arcH = Math.min(cw * 2.4, 30 + dist * 0.32);
        const rand = seeded(`${ctx.seed}:enter`);
        const spin = (rand() < 0.5 ? -1 : 1) * (dist > cw * 2 ? Math.PI * 0.25 : 0);
        const yawStart = yawEnd + spin - turnOf(zone, land) * 0.6;

        // 表裏: A = 置かれる姿（盤のカードと同じ絵）、B = その裏側
        const player = to?.player ?? payload.player ?? 0;
        const other = { ...land, faceUp: !land.faceUp };
        const flips = startFaceUp(payload) !== land.faceUp;
        const g = new THREE.Group();          // 位置・向き（y 軸）
        const tilt = new THREE.Group();       // 進む向きへの傾き
        const flipper = new THREE.Group();    // 長い辺を軸に返す
        add(root, g, 30);
        g.add(tilt);
        tilt.add(flipper);
        const a = faceMesh(ctx, { zone, player, c: land, w: cw, h: ch });
        add(flipper, a, 30);
        const b = flips ? faceMesh(ctx, { zone, player, c: other, w: cw, h: ch }) : null;
        if (b) {
          const bb = new THREE.Group();
          bb.rotation.z = Math.PI;             // 下を向いて待つ（返ったときに上を向く）
          bb.add(b);
          add(flipper, bb, 30);
        }
        [g, tilt, flipper].forEach((o) => { o.renderOrder = 30; });

        // 着地の硬い輪（カードの縁の白い線が外へ広がる）と火花（セットは出さない）
        const ringBars = quiet ? [] : [0, 1, 2, 3].map(() => {
          const m = ctx.kit.onBoard(fill(ctx, '#ffffff', 1, 1));
          add(root, m, 31);
          return m;
        });
        const sparks = quiet ? [] : Array.from({ length: SPARKS }, (_, i) => {
          const ang = (i / SPARKS) * Math.PI * 2 + (rand() - 0.5) * 0.5;
          const m = ctx.kit.onBoard(fill(ctx, i % 3 === 0 ? '#ffd21a' : '#ffffff', 1, 1));
          add(root, m, 32);
          return { m, dir: [Math.cos(ang), Math.sin(ang)], reach: cw * (0.85 + rand() * 0.6), len: cw * (0.16 + rand() * 0.14) };
        });

        const cosY = Math.cos(yawEnd); const sinY = Math.sin(yawEnd);
        // カードの座標（x = 絵の右、z = 絵の下）→ ワールド（y 軸の回しは three と同じ向き）
        const rotate = ([x, z]) => [end[0] + x * cosY + z * sinY, end[2] - x * sinY + z * cosY];

        return {
          root,
          update: ({ localMs: t }) => {
            // 飛ぶ: はじめは素早く離れ、置かれる手前で加速して置く（弧のてっぺんで一番高い）
            // 位置の進みは ease-out（入りは速く、置く手前でゆっくり）。置く直前に軽く沈んで 340 で盤のカードの高さちょうど
            const k = flightAt(t, src.via);
            const up = Math.sin(Math.PI * k) * arcH;
            const x = start[0] + (end[0] - start[0]) * k;
            const y = start[1] + (end[1] - start[1]) * k + up - dipAt(t);
            const z = start[2] + (end[2] - start[2]) * k;
            g.position.set(x, y, z);
            g.rotation.y = yawStart + (yawEnd - yawStart) * tween(t, APPEAR, LAND - 40, 'ease_in_out');
            // 進む向きへ少し傾いて、置かれるときには水平
            tilt.rotation.x = 0.35 * Math.sin(Math.PI * k) * (src.via === 'above' ? 0.5 : 1);
            flipper.rotation.z = flips ? Math.PI * (1 - tween(t, APPEAR + 20, LAND - 60, 'ease_in_out')) : 0;
            const faceA = Math.cos(flipper.rotation.z) >= 0;
            // 現れる: 小さく薄いところから
            const pop = 0.7 + 0.3 * tween(t, 0, APPEAR, 'expo_out');
            g.scale.set(pop, pop, pop);
            const on = t < LAND ? seg(t, 0, APPEAR * 0.6) : 0;
            fade(a, faceA ? on : 0);
            if (b) fade(b, faceA ? 0 : on);

            // 置かれた瞬間から: 輪（カードの 4 辺）が外へ広がって細くなる
            const rk = tween(t, LAND, D - 6, 'expo_out');
            const thin = 1 - seg(t, LAND + 10, D - 6);
            const show = t >= LAND && t < D - 6;
            const hw = (cw / 2) * (1 + 0.28 * rk);
            const hh = (ch / 2) * (1 + 0.28 * rk);
            const turn = turnOf(zone, land);
            // 輪はカードの形（盤の向き・守備の横向きごと）
            const [ew, eh] = Math.abs(Math.sin(turn)) > 0.5 ? [hh, hw] : [hw, hh];
            const cos0 = Math.cos(base); const sin0 = Math.sin(base);
            const rot0 = ([px, pz]) => [end[0] + px * cos0 + pz * sin0, end[2] - px * sin0 + pz * cos0];
            const corners = [[-ew, -eh], [ew, -eh], [ew, eh], [-ew, eh]].map(rot0);
            ringBars.forEach((m, i) => {
              lay(m, corners[i], corners[(i + 1) % 4], cw * 0.03 * thin, yTop + 0.3);
              fade(m, show ? 1 : 0);
            });
            // 火花: カードの縁から外へ飛ぶ細い線
            const sk = tween(t, LAND, D - 6, 'expo_out');
            sparks.forEach((s) => {
              const r0 = cw * 0.5;
              const head = [s.dir[0] * (r0 + s.reach * sk), s.dir[1] * (r0 + s.reach * sk)];
              const tail = [head[0] - s.dir[0] * s.len * thin, head[1] - s.dir[1] * s.len * thin];
              lay(s.m, rotate(tail), rotate(head), cw * 0.02 * thin, yTop + 0.4);
              fade(s.m, show && thin > 0.02 ? 1 : 0);
            });
          },
        };
      },
    },
  ],
}, 'move_enter_motion.js');
